import angular from "angular";

import { Arrays, Calculations, MessageThreads, Payrolls} from "@salaxy/core";

import { MessageThreadCrudController } from "../communications";

import { SessionService, UiHelpers, UploadService } from "../../services";

/** DEMO / PROTO ONLY: UI prototype for worker side of new Timesheet and usecases functionality. */
export class TimesheetWorkerDemoController extends MessageThreadCrudController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["MessageThreads", "UiHelpers", "UploadService", "$location", "$routeParams", "$anchorScroll", "$timeout", "SessionService", "$interval", "Calculations", "Payrolls"];

  /** List of entries: This should be stored in local storage. */
  public entries = [];

  /** Current entry in the entry or editable entry */
  public currentEntry: {
    start: Date,
    end?: Date,
    total: Date,
    project?: string,
    comment?: string,
    isStarted: boolean,
  } = null;

  public projects = {
    "1234": "Projekti 1",
    "2345": "Toinen projekti",
    "1345": "Joku muu projekti",
  };

  private timer: angular.IPromise<void>;

  /** Constructor for dependency injection */
  constructor(
    private msgApi: MessageThreads,
    uiHelpers: UiHelpers,
    uploadService: UploadService,
    $location: angular.ILocationService,
    $routeParams: any,
    $anchorScroll: angular.IAnchorScrollService,
    $timeout: angular.ITimeoutService,

    sessionService: SessionService,
    calculations: Calculations,
    payrolls: Payrolls,
    private $interval: angular.IIntervalService,
  ) {
    super(msgApi, uiHelpers, uploadService, $location, $routeParams, $anchorScroll, $timeout, sessionService, calculations, payrolls);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.resetTimer();
    super.$onInit();
  }

  /** Called when controller is destroyed */
  public $onDestroy = () => {
    this.stopTechnicalTimer();
  };

  /** Resets the timer to zero value */
  public resetTimer() {
    this.stopTechnicalTimer();
    this.currentEntry = {
      isStarted: false,
      start: this.roundDate(),
      total: new Date(0),
    };
  }

  /** Starts the timer. */
  public startTimer()
  {
    this.stopTechnicalTimer();
    this.currentEntry.start = this.roundDate();
    this.currentEntry.isStarted = true;
    this.timer = this.$interval(() => {
      this.currentEntry.total = new Date(this.roundDate().getTime() - this.currentEntry.start.getTime());
    }, 1000);
  }

  /** Starts the timer. */
  public stopTimer()
  {
    this.currentEntry.end = this.roundDate();
    this.currentEntry.total = new Date(this.currentEntry.end.getTime() - this.currentEntry.start.getTime());
    this.entries.push(this.currentEntry);
    this.resetTimer();
  }

  /** Gets the project text for a project */
  public getProjectText(key: string): string {
    if (!key) {
      return "Ei projektia";
    }
    return this.projects[key] || ("Ei projektia " + key) ;
  }

  /** Gets the total of the timesheets logged today */
  public getTotal() {
    return new Date(Arrays.sum(this.entries, (x) => x.total.getTime()));
  }

  private roundDate(time: Date = null): Date {
    if (!time) {
      time = new Date();
    }
    time.setMilliseconds(0);
    // time.setSeconds(0);
    return time;
  }

  private stopTechnicalTimer() {
    if (this.timer) {
      this.$interval.cancel(this.timer);
    }
  }


  public calendarDemoData = [
    {
      day: "ma",
      date: "2.5.",
      hours: "7:30 h",
    },{
      day: "ti",
      date: "3.5.",
      hours: "7:30 h",
    },{
      day: "ke",
      date: "4.5.",
      hours: null,
    },{
      day: "to",
      date: "5.5.",
      hours: null,
    },{
      day: "pe",
      date: "6.5.",
      hours: null,
    },{
      day: "la",
      date: "7.5.",
      hours: null,
      isHoliday: true,
    },{
      day: "su",
      date: "8.5.",
      hours: null,
      isHoliday: true,
    },
  ];

  /** Sets the usecase to a new message thread */
  public setUsecase(newUsecase: any) {
    (this.current as any).usecase = {
      uri: "palkkaus.fi/testing/messages-proto/" + newUsecase.id,
      label: newUsecase.title,
      description: newUsecase.descr,
      data: {
        protoId: newUsecase.id,
      },
    };
  }

  /** Gets the usecases available for the current Worker-Employer combination. */
  public getUsecases(): {
    id: string,
    icon?: string,
    pic?: string,
    title: string,
    descr: string,
  }[] {
    return this.uiHelpers.cache(this, "usecases", () => {
      return [
        {
          id: "default",
          icon: "fa-envelope",
          title: "Viesti",
          descr: "Lähetä viesti turvapostilla.",
        },
        {
          id: "timer",
          icon: "fa-clock-o",
          title: "Kellokortti",
          descr: "Leimaa sisään digitaalisella kellokorttisovelluksella.",
        },
        {
          id: "timesheet",
          icon: "fa-calendar",
          title: "Kirjaa tunteja",
          descr: "Kirjaa / muokkaa jo tapahtuneita tunteja.",
        },
        {
          id: "timesheet",
          icon: "fa-calendar",
          title: "Kirjaa työaikoja",
          descr: "Kirjaa / muokkaa jo tehtyjä työaikoja (myös kellonajat).",
        },
        {
          id: "timesheet",
          icon: "fa-sun-o",
          title: "Lomakalenteri",
          descr: "Lähetä lomapyyntö",
        },
        {
          id: "timesheet",
          icon: "fa-ambulance",
          title: "Poissaolot",
          descr: "Ilmoita poissaolosta",
        },
        {
          id: "travel",
          icon: "fa-suitcase",
          title: "Matkalaskut",
          descr: "Kilometrikorvaukset, päivärahat ja kulukorvaukset matkoista.",
        },
        {
          id: "expenses",
          icon: "fa-shopping-cart",
          title: "Kulukorvaukset",
          descr: "Kulukorvaukset kuittien perusteella mukaan lukien kilometrikorvaukset.",
        },
        {
          id: "calc",
          pic: "https://salaxypublic.blob.core.windows.net/salaxy-wordpress/2019/03/palkkaennakko_palkkalaskuri_palkkalaskelma.png",
          title: "Palkkalaskuri",
          descr: "Palkkaus.fi perinteinen palkkalaskuri, jolla voit lähettää minkä tahansa palkkapyynnön.",
        }
      ];
    });
  }

}
